import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useStatementStore, resolveColumnState } from "../store/statementStore";

/**
 * Inline editable transaction table.
 *
 * Behavior:
 *   - Each debit/credit cell is a small numeric input.
 *   - As the user types, the value is staged in `pendingEdits`.
 *   - On blur (or Enter), we flush to the backend which recomputes the
 *     downstream balances. The new values come back via the statement
 *     and re-render with a small ticker animation.
 *   - The balance cells are read-only (computed).
 */
function fmt(num, fmt) {
    if (num == null) return "";
    const sign = num < 0;
    const abs = Math.abs(num);
    const parts = abs.toFixed(fmt?.decimals ?? 2).split(".");
    if (fmt?.has_thousands) {
        if (fmt.indian_grouping && parts[0].length > 3) {
            const last3 = parts[0].slice(-3);
            let rest = parts[0].slice(0, -3);
            const groups = [];
            while (rest.length > 2) {
                groups.unshift(rest.slice(-2));
                rest = rest.slice(0, -2);
            }
            if (rest) groups.unshift(rest);
            parts[0] = groups.join(",") + "," + last3;
        } else {
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
    }
    let out = parts.join(".");
    if (sign) out = fmt?.paren_negative ? `(${out})` : `-${out}`;
    return (fmt?.prefix || "") + out + (fmt?.suffix || "");
}

function parseInput(raw) {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const cleaned = s.replace(/[^0-9.,()-]/g, "");
    let neg = false;
    let c = cleaned;
    if (c.startsWith("(") && c.endsWith(")")) {
        neg = true;
        c = c.slice(1, -1);
    }
    if (c.startsWith("-")) {
        neg = true;
        c = c.slice(1);
    }
    // Strip thousands separators (treat last . or , as decimal)
    if (c.includes(",") && c.includes(".")) {
        if (c.lastIndexOf(",") > c.lastIndexOf(".")) {
            c = c.replace(/\./g, "").replace(",", ".");
        } else {
            c = c.replace(/,/g, "");
        }
    } else if (c.includes(",")) {
        const last = c.split(",").pop();
        if (last.length <= 2 && c.split(",").length === 2) {
            c = c.replace(",", ".");
        } else {
            c = c.replace(/,/g, "");
        }
    }
    const v = parseFloat(c);
    if (Number.isNaN(v)) return null;
    return neg ? -v : v;
}

function AmountCell({ tx, field, fmt }) {
    const { stageEdit, flushEdits, pendingEdits } = useStatementStore();
    const staged = pendingEdits[tx.id] || {};
    const stagedValue = staged[field];
    const clearedKey = field === "debit" ? "edited_debit_cleared" : "edited_credit_cleared";
    const dashKey = field === "debit" ? "edited_debit_zero_dash" : "edited_credit_zero_dash";
    const origDashKey = field === "debit" ? "debit_zero_dash" : "credit_zero_dash";
    const column = resolveColumnState(tx, field, staged);
    const explicit =
        stagedValue !== undefined ||
        staged[`${field}_cleared`] !== undefined ||
        staged[`${field}_zero_dash`] !== undefined ||
        (tx[field === "debit" ? "edited_debit" : "edited_credit"] != null
            && tx[field === "debit" ? "edited_debit" : "edited_credit"] !== undefined) ||
        Boolean(tx[clearedKey]) ||
        Boolean(tx[dashKey]);

    const formatDraft = React.useCallback((col) => {
        if (col.empty) return "";
        if (col.dash) return "-";
        if (col.value == null) return "";
        return typeof fmt === "function" ? fmt(col.value) : String(col.value);
    }, [fmt]);

    const [draft, setDraft] = React.useState(formatDraft(column));
    const [isFocused, setIsFocused] = React.useState(false);
    const debounceTimerRef = React.useRef(null);

    React.useEffect(() => {
        if (!isFocused) {
            setDraft(formatDraft(resolveColumnState(tx, field, staged)));
        }
    }, [tx, field, staged, formatDraft, isFocused]);

    React.useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    function onChange(e) {
        const raw = e.target.value;
        setDraft(raw);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(async () => {
            if (raw.trim() === "-") {
                stageEdit(tx.id, field, 0, { zeroDash: true });
            } else {
                const parsed = parseInput(raw);
                stageEdit(tx.id, field, parsed);
            }
            await flushEdits();
        }, 350);
    }

    async function commit() {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        const raw = draft;
        if (raw.trim() === "-") {
            stageEdit(tx.id, field, 0, { zeroDash: true });
        } else {
            const parsed = parseInput(raw);
            stageEdit(tx.id, field, parsed);
        }
        await flushEdits();
    }

    const isEdited = explicit;
    const isStaged = stagedValue !== undefined;

    return (
        <input
            className={`amount-input ${isEdited || isStaged ? "edited" : ""}`}
            value={draft}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={async () => {
                setIsFocused(false);
                await commit();
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.target.blur();
                }
            }}
            placeholder="0.00"
            data-testid={`tx-${field}-${tx.row_index}`}
        />
    );
}

function SwapAmountsButton({ tx }) {
    const { swapAmounts, flushEdits, pendingEdits } = useStatementStore();
    const staged = pendingEdits[tx.id] || {};
    const debit = resolveColumnState(tx, "debit", staged);
    const credit = resolveColumnState(tx, "credit", staged);
    const canSwap = !debit.empty || !credit.empty || debit.dash || credit.dash;
    if (!canSwap) return null;

    return (
        <button
            type="button"
            className="swap-amounts-btn"
            title="Swap debit and credit"
            onMouseDown={(e) => e.preventDefault()}
            onClick={async () => {
                swapAmounts(tx.id);
                await flushEdits();
            }}
            data-testid={`tx-swap-${tx.row_index}`}
        >
            ⇄
        </button>
    );
}

/**
 * Inline editable text cell for description and date fields.
 * Preserves the exact font/size when written back to the PDF.
 */
const TEXT_FIELD_ORIGINAL = {
    description: "description",
    reference: "reference",
    date: "date",
};
const TEXT_FIELD_EDITED = {
    description: "edited_description",
    reference: "edited_reference",
    date: "edited_date",
};

function TextCell({ tx, field, placeholder = "", testId }) {
    const { stageTextEdit, flushEdits, pendingTextEdits } = useStatementStore();
    const stagedValue = pendingTextEdits?.[tx.id]?.[field];
    const originalValue = tx[TEXT_FIELD_ORIGINAL[field]];
    const editedValue = tx[TEXT_FIELD_EDITED[field]];

    const current =
        stagedValue !== undefined
            ? stagedValue
            : editedValue !== null && editedValue !== undefined
              ? editedValue
              : originalValue;

    const [draft, setDraft] = React.useState(current || "");
    const [focused, setFocused] = React.useState(false);

    React.useEffect(() => {
        if (!focused) {
            setDraft(current || "");
        }
    }, [current, focused]);

    function onChange(e) {
        setDraft(e.target.value);
        stageTextEdit(tx.id, field, e.target.value);
    }

    async function commit() {
        setFocused(false);
        await flushEdits();
    }

    const isEdited = (editedValue !== null && editedValue !== undefined) || stagedValue !== undefined;

    return (
        <input
            className={`text-cell-input ${isEdited ? "edited" : ""}`}
            value={draft}
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
                if (e.key === "Escape") {
                    setDraft(originalValue || "");
                    stageTextEdit(tx.id, field, null);
                    e.target.blur();
                }
            }}
            placeholder={placeholder || "—"}
            data-testid={testId}
        />
    );
}

const TransactionRow = React.memo(function TransactionRow({ tx, fmt, formatWith }) {
    const isAnchor =
        tx.debit == null &&
        tx.credit == null &&
        tx.edited_debit == null &&
        tx.edited_credit == null;
    return (
        <div
            className={`grid grid-cols-[100px_120px_1fr_120px_28px_120px_140px] gap-0 items-center hover:bg-white/[0.025] transition-colors group ${tx.text_edited ? "text-edited-row" : ""}`}
            style={{ contentVisibility: "auto", containIntrinsicSize: "44px" }}
            data-testid={`tx-row-${tx.row_index}`}
        >
            {/* Date — editable text */}
            <div className="px-2 py-1.5">
                <TextCell
                    tx={tx}
                    field="date"
                    placeholder={tx.date || "—"}
                    testId={`tx-date-${tx.row_index}`}
                />
            </div>

            {/* Cheque / reference — editable text */}
            <div className="px-2 py-1.5">
                <TextCell
                    tx={tx}
                    field="reference"
                    placeholder={isAnchor ? "—" : "cheque / ref"}
                    testId={`tx-reference-${tx.row_index}`}
                />
            </div>

            {/* Description — editable text */}
            <div className="px-2 py-1.5">
                <TextCell
                    tx={tx}
                    field="description"
                    placeholder={isAnchor ? "—" : "description"}
                    testId={`tx-description-${tx.row_index}`}
                />
            </div>

            {/* Debit — always editable */}
            <div className="px-2 py-1.5 text-right">
                <AmountCell tx={tx} field="debit" fmt={(v) => formatWith(v, fmt)} />
            </div>

            <div className="flex items-center justify-center py-1.5">
                <SwapAmountsButton tx={tx} />
            </div>

            {/* Credit — always editable */}
            <div className="px-2 py-1.5 text-right">
                <AmountCell tx={tx} field="credit" fmt={(v) => formatWith(v, fmt)} />
            </div>

            {/* Balance — read-only, recomputed */}
            <div className="px-4 py-2.5 text-right">
                <span
                    className={`font-mono text-[13px] inline-block transition-colors duration-150 ${
                        tx.balance_dirty
                            ? "text-amber-300 font-medium"
                            : "text-zinc-100"
                    }`}
                    data-testid={`tx-balance-${tx.row_index}`}
                >
                    {tx.display_balance ||
                        (tx.computed_balance != null
                            ? formatWith(tx.computed_balance, fmt)
                            : "")}
                </span>
            </div>
        </div>
    );
});

function formatWith(num, f) {
    if (num == null) return "";
    const sign = num < 0;
    const abs = Math.abs(num);
    const parts = abs.toFixed(f?.decimals ?? 2).split(".");
    if (f?.has_thousands) {
        if (f.indian_grouping && parts[0].length > 3) {
            const last3 = parts[0].slice(-3);
            let rest = parts[0].slice(0, -3);
            const groups = [];
            while (rest.length > 2) {
                groups.unshift(rest.slice(-2));
                rest = rest.slice(0, -2);
            }
            if (rest) groups.unshift(rest);
            parts[0] = groups.join(",") + "," + last3;
        } else {
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
    }
    let out = parts.join(".");
    if (sign) out = f?.paren_negative ? `(${out})` : `-${out}`;
    return (f?.prefix || "") + out + (f?.suffix || "");
}

export default function TransactionTable() {
    const { statement } = useStatementStore();
    if (!statement) return null;
    const fmt = statement.number_format;

    return (
        <div className="rounded-lg overflow-hidden border border-white/[0.08] bg-[#0d0d0d] min-w-[900px]">
            {/* Header */}
            <div className="grid grid-cols-[100px_120px_1fr_120px_28px_120px_140px] gap-0 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 border-b border-white/[0.08] bg-black/40">
                <div className="px-3 py-3">Date</div>
                <div className="px-3 py-3">Cheque / Ref</div>
                <div className="px-4 py-3">Description</div>
                <div className="px-3 py-3 text-right">Debit</div>
                <div />
                <div className="px-3 py-3 text-right">Credit</div>
                <div className="px-4 py-3 text-right">Balance</div>
            </div>
            <div className="divide-y divide-white/[0.05]" data-testid="transactions-body">
                {statement.transactions.map((tx) => (
                    <TransactionRow
                        key={tx.id}
                        tx={tx}
                        fmt={fmt}
                        formatWith={formatWith}
                    />
                ))}
            </div>
        </div>
    );
}
